import { useState, useEffect, useCallback } from 'react';
import { getAllProducts } from '@/services/products.service';
import { groupProductsByCategory, type CategoryGroup } from '@/services/api-adapter';
import { ofertasPrimero, ordenAleatorio } from '@/utils/helpers';

export type { CategoryGroup };

interface UseProductsByCategoryReturn {
  groups: CategoryGroup[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useProductsByCategory(): UseProductsByCategoryReturn {
  const [groups, setGroups] = useState<CategoryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGroups = useCallback(() => {
    setLoading(true);
    setError(null);

    getAllProducts()
      .then((allProducts) => {
        // Categorías de la pestaña Tienda, las de más productos primero
        const filtered = groupProductsByCategory(allProducts);

        // Put kits first
        const KIT_KEYWORDS = ['kit', 'kits'];
        const isKit = (g: CategoryGroup) =>
          KIT_KEYWORDS.some((k) => g.categoryId.includes(k) || g.categoryName.toLowerCase().includes(k));
        const kits = filtered.filter(isKit);
        const rest = filtered.filter((g) => !isKit(g));
        // Dentro de cada categoría: ofertas primero y el resto en orden aleatorio
        const result = [...kits, ...rest].map((g) => ({
          ...g,
          products: ofertasPrimero(ordenAleatorio(g.products)),
        }));

        setGroups(result);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Error al cargar productos');
        setLoading(false);
      });
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Re-fetch when window regains focus (user was away, products may have changed)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(() => fetchGroups(), 300);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchGroups]);

  return { groups, loading, error, refetch: fetchGroups };
}
